import type { ReactNode } from 'react'
import type { Messages } from './ko'

export const en: Messages = {
  app: {
    layout: {
      settings: 'Settings',
    },
    update: {
      message: 'mlog has changed a little. You can see the changes when you reopen it.',
      reopening: 'Reopening',
      reopen: 'Reopen',
    },
    notFound: {
      message: "That page isn't here",
      back: 'Back to my entries',
    },
    common: {
      retry: 'Try again',
    },
  },

  auth: {
    gate: {
      opening: 'One moment, opening your space',
      unreachable: "The connection isn't working right now. You can try again in a moment.",
    },
    login: {
      /**
       * 한국어 정본의 나만의를 여기서 옮기지 않고 안심 문장에 맡긴다. 소개와 안심 문장이 모두
       * 소유격을 쓰면 광고 문구로 읽히고 줄 수도 예산을 넘는다.
       */
      tagline: "A quiet place for what's on your mind",
      assurance: 'Nothing written here is visible to anyone else.',
      establishing: 'Getting ready to go in',
      autoSignUp: "If there's no account yet, this button makes one.",
      /**
       * 주어를 세우지 않는다. By continuing, you agree로 적으면 이 화면에서 사용자를 부르는
       * 말이 한 번 더 늘고 문장이 세 줄로 넘어간다.
       */
      consent: (terms: ReactNode, privacy: ReactNode): ReactNode => (
        <>
          Continuing means agreeing to the {terms} and {privacy}.
        </>
      ),
      termsLink: 'Terms of Service',
      privacyLink: 'Privacy Policy',
      sessionExpired:
        'The sign-in ended after a while. Everything is still here when you sign in again.',
    },
    failure: {
      declined: "That's all right. You can try again whenever you're ready.",
      emailAlreadyRegistered:
        'A space with this email already exists. You can sign in with the method you used first, then add this one in Settings.',
      emailUnavailable: (provider: string) =>
        `The email on the ${provider} account couldn't be confirmed, so the space isn't open yet. You can finish confirming the email on ${provider}, then try again.`,
      unavailable: "The sign-in didn't finish. You can try again.",
    },
    provider: {
      google: 'Google',
      /**
       * 로마자 문자표를 쓰는 화면에서는 이 회사가 스스로 쓰는 표기가 대문자 NAVER다. 한국어
       * 정본이 한글 네이버를 쓰는 근거와 같은 근거로 여기서는 NAVER로 적는다.
       */
      naver: 'NAVER',
      continueWith: (provider: string) => `Continue with ${provider}`,
      /**
       * 떠나는 일에 open을 쓰지 않는다. 이 사전에서 open은 공간을 여는 일과 기록을 여는 일에
       * 이미 서 있다. 누른 버튼 안에서 라벨을 대신하므로 한 줄을 넘기지 않는다.
       */
      leavingTo: (provider: string) => `Going to ${provider}`,
    },
    logout: {
      label: 'Sign out',
      pending: 'Signing out',
      failed: "The sign-out didn't finish. You can try again.",
    },
  },

  onboarding: {
    /**
     * 소유격을 이 화면에서 한 번만 쓴다. 인사가 그 한 번을 가져가고 나머지 줄은 사실만 적는다.
     */
    greeting: 'This space is yours alone.',
    from: (provider: string) => `You're signed in with ${provider}.`,
    /**
     * 명령형을 쓰지 않는다. Add로 열면 이 화면이 시키는 자리가 되고 건너뛰기가 거절이 된다.
     */
    suggest: (provider: string) =>
      `With ${provider} added too, either one brings you to this same space.`,
    add: (provider: string) => `Add ${provider}`,
    skip: 'Just go in',
    later: 'You can add it in Settings anytime.',
  },

  link: {
    addedNotice: (provider: string) => `${provider} was added to your sign-in methods.`,
    close: 'Close',
  },

  entry: {
    list: {
      compose: 'Write something',
      loading: 'Loading the entries',
      failed: "The entries didn't load. You can try again in a moment.",
      /**
       * 한국어 정본은 물음표를 떼어 두었는데 영어에서 물음표 없는 의문문은 고장으로 읽힌다.
       * 부호는 언어마다 그 언어의 관습을 따른다.
       */
      empty: 'How has today been?',
      emptyWelcome: 'The space is open. How has today been?',
      loadMore: 'See more past entries',
      loadingMore: 'Loading',
    },
    editor: {
      opening: 'Opening the entry',
      openFailed: "The entry didn't open",
      moodHint: 'You can pick whichever feels closest. Skipping it is fine too.',
      placeholder: 'Write whatever comes to mind',
      bodyLabel: 'Entry body',
      saving: 'Saving',
      saved: 'Saved',
      saveFailed: "Couldn't save",
    },
    /**
     * 칩 안에 한 줄로 서므로 한 낱말로 둔다. 임상 용어로 읽히는 anxious와 depressed를 피하고
     * 일상어를 쓴다.
     */
    mood: {
      calm: 'Calm',
      glad: 'Glad',
      tired: 'Tired',
      sad: 'Low',
      anxious: 'Uneasy',
    },
  },

  settings: {
    screen: {
      title: 'Settings',
      back: 'Back',
    },
    language: {
      label: 'Language',
      sheetTitle: 'Language',
      sheetHint: 'Only the words on screen change. Your entries and nickname stay.',
      system: 'System default',
      saveFailed: "Couldn't save the language setting to your account. You can try again.",
      close: 'Close',
    },

    nickname: {
      /* Name은 신원을 적는 칸의 말이고 Display name은 어디에 보이느냐를 되묻게 만든다. */
      label: 'Nickname',
      sheetTitle: 'Nickname',
      /* 남이 붙여 준 말이라는 결이 Nickname에 얼마간 남아 your와 pick으로 주인을 세운다. */
      sheetHint: 'Only you can see your nickname. You can pick anything you like.',
      /* 무엇을 다시 짓는지 붙이지 않아 한 줄에 든다. Regenerate는 기계의 말이라 쓰지 않는다. */
      regenerate: 'Try another',
      regenerating: 'Making a nickname',
      regenerateFailed: "Couldn't make a nickname. You can try again.",
      save: 'Save',
      saving: 'Saving',
      saveFailed: "Couldn't save your nickname. You can try again.",
      loadFailed: "Couldn't load your nickname.",
      /* 수에 따라 문장이 갈리는 분기를 이 함수 안에서 끝낸다. 조각을 이어 붙이지 않는다. */
      remaining: (count: number) => (count === 1 ? '1 character left' : `${count} characters left`),
      over: (count: number) =>
        count === 1 ? 'Just 1 character too many' : `Just ${count} characters too many`,
      close: 'Close',
    },
    /**
     * log를 쓰지 않는다. 이 제품에서 log는 기록을 뜻하고 이름이 그 뜻으로 서 있어 한 낱말이
     * 두 뜻을 진다. connect와 link, accounts도 뺐다. 앞의 둘은 한국어 연결이 진 함정을 그대로
     * 지고 accounts는 이 목록이 답할 질문과 어긋난다.
     */
    loginMethod: {
      label: 'Sign-in methods',
      description: 'Any method you add brings you to this same space.',
      added: 'Added',
      add: 'Add',
      addName: (provider: string) => `Add ${provider}`,
      linkedName: (provider: string) => `You can sign in with ${provider}`,
      addedNotice: (provider: string) => `${provider} was added.`,
      /** space가 저장 용량으로 읽힐 자리라 이 한 문장에서만 mlog를 붙인다. */
      alreadyLinked: (provider: string) =>
        `This ${provider} account already belongs to another space in mlog. You can try a different account.`,
      failed: "Adding didn't go through. You can try again.",
      loadFailed: "Couldn't load your sign-in methods.",
    },
    theme: {
      /**
       * 무드 라벨과 낱말이 겹치지 않게 mood를 쓰지 않는다. 한국어 정본이 분위기와 기분을
       * 갈라 둔 것과 같은 자리다.
       */
      label: 'Atmosphere',
      light: 'Clear day',
      dark: 'Quiet night',
      dusk: 'Evening glow',
    },
    legal: {
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
    },
  },

  legal: {
    document: {
      back: 'Back',
      effectiveDate: (date: string) => `Effective ${date}`,
    },
  },
}
